import { CheckboxGroupMask } from "@/components/form-items/CheckboxGroupMask";
import { useCountAllRelatedFields } from "@/api/related-fields";
import { useTranslation } from "react-i18next";
import { Tirage } from "@/api/tirages/tirage.api";
import {
  ConfigurationTypeFile,
  FieldConfig,
  isLayoutElement,
} from "@/config/forms/general";
import {
  Artwork,
  Auction,
  Audiovisual,
  Author,
  Bibliography,
  Collector,
  Document,
  Exhibition,
  Multipleprint,
  Multiplesample,
  Photography,
  Writing,
  SculptureEdition,
} from "@/lib/types";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ArtworkInAuctionLinks from "./links/ArtworkInAuction";
import ArtworkInBibliographyLinks from "./links/ArtworkInBibliography";
import ArtworkInExhibitions from "./links/ArtworkInExhibitions";
import ArtworkLinks from "./links/ArtworkLinks";
import AuctionLinks from "./links/AuctionLinks";
import AudiovisualLinks from "./links/AudiovisualLinks";
import AuthorLinks from "./links/AuthorLinks";
import BibliographyLinks from "./links/BibliographyLinks";
import CollectorInMultipleprints from "./links/CollectorInMultipleprint";
import DocumentInBibliographyLinks from "./links/DocumentInBibliography";
import BibliographyInBibliographyLinks from "./links/BibliographyInBibliography";
import DocumentLinks from "./links/DocumentLinkts";
import ExhibitionLinks from "./links/ExhibitionLinks";
import ExternalLinks from "./links/ExternalLinks";
import MultipleLinks from "./links/MultipleLinks";
import MultipleprintInAuctionLinks from "./links/MultipleprintInAuctionLinks";
import MultipleprintInBibliographyLinks from "./links/MultipleprintInBibliography";
import MultipleprintInExhibtionLinks from "./links/MultipleprintInExhibtionLinks";
import MultipleprintInMultiplesampleLinks from "./links/MultipleprintInMultiplesampleLinks";
import MultiplesampleInAuctionLinks from "./links/MultiplesampleInAuctionLinks";
import MultiplesampleInBibliographyLinks from "./links/MultiplesampleInBibliographyLinks";
import MultiplesampleInExhibitionLinks from "./links/MultiplesampleInExhibitionLinks";
import MultiplesampleLinks from "./links/MultiplesampleLinks";
import PhotographiesLinks from "./links/PhotographiesLinks";
import PhotographyInBibliographyLinks from "./links/PhotographyInBibliography";
import TirageInAuctionLinks from "./links/TirageInAuction";
import TirageInBibliographyLinks from "./links/TirageInBibliography";
import TirageInExhibitionLinks from "./links/TirageInExhibition";
import TirageLinks from "./links/TirageLinks";
import SculptureEditionInAuction from "./links/SculptureEditionInAuction";
import SculptureEditionInBibliography from "./links/SculptureEditionInBibliography";
import SculptureEditionInExhibitions from "./links/SculptureEditionInExhibitions";
import SculptureEditionLinks from "./links/SculptureEditionLinks";
import WritingLinks from "./links/WritingLinks";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

export type LinkableData =
  | Artwork
  | Multipleprint
  | Multiplesample
  | Photography
  | Audiovisual
  | Document
  | Writing
  | Author
  | Exhibition
  | Auction
  | Collector
  | Bibliography
  | Tirage
  | SculptureEdition;
export type LinkableType =
  | "artwork"
  | "multipleprint"
  | "multiplesample"
  | "photography"
  | "audiovisual"
  | "document"
  | "exhibition"
  | "bibliography"
  | "author"
  | "auction"
  | "collector"
  | "writing"
  | "tirage"
  | "sculptureEdition";

interface LinksManagementProps {
  id?: string;
  configuration: ConfigurationTypeFile | null | undefined;
  data: LinkableData;
  type: LinkableType;
  className?: string;
  /** Fiche exemplaire : masque N° enregistrement et résumé exemplaire dans le tableau des expos liées. */
  hideExemplaireColumnsInExhibitionLinks?: boolean;
}

/** Single link tab config (from FieldConfig.links[number]) */
type LinkConfig = NonNullable<FieldConfig["links"]>[number];

const LinksManagement = ({
  id,
  data,
  configuration,
  type,
  className,
  hideExemplaireColumnsInExhibitionLinks,
}: LinksManagementProps) => {
  const { t } = useTranslation();
  const config = configuration?.fields.find(
    (field): field is FieldConfig & { type: "links"; links: NonNullable<FieldConfig["links"]> } =>
      !isLayoutElement(field) &&
      (field as FieldConfig).type === "links" &&
      "links" in field,
  );
  const { isLoading, data: countRelatedFields } = useCountAllRelatedFields(
    id,
    type,
    config?.links?.map((link: LinkConfig) => link.name) ?? [],
  );
  const links: LinkConfig[] | undefined = config?.links?.filter(
    (link: LinkConfig) => !link.hidden,
  );

  const defaultTab = useMemo(() => links?.[0]?.name, [links]);

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [searchParams, setSearchParam] = useSearchParams();

  useEffect(() => {
    const view = searchParams.get("linkTab");

    if (view == null) {
      setActiveTab(defaultTab);
      return;
    }
    if (links?.map((t: LinkConfig) => t.name).includes(view)) {
      setActiveTab(view);
      return;
    }
  }, [searchParams, setActiveTab, links, defaultTab]);

  const components = useMemo(() => {
    switch (activeTab) {
      case "relatedArtwork":
        return <ArtworkLinks id={id ?? ""} type={type} />;
      case "relatedAudiovisual":
        return <AudiovisualLinks id={id ?? ""} type={type} />;
      case "relatedDocument":
        return <DocumentLinks id={id ?? ""} type={type} />;
      case "relatedWriting":
        return <WritingLinks id={id ?? ""} type={type} />;
      case "relatedPhotography":
        return <PhotographiesLinks id={id ?? ""} type={type} />;
      case "relatedSculptureEditions":
        return <SculptureEditionLinks id={id ?? ""} type={type} />;
      case "relatedMultipleprint":
        return <MultipleLinks id={id ?? ""} type={type} />;
      case "multipleprintInMultipleSample":
        return <MultipleprintInMultiplesampleLinks id={id ?? ""} type={type} />;
      case "relatedMultiplesample":
        return <MultiplesampleLinks id={id ?? ""} type={type} />;
      case "relatedMultipleSample":
        return <MultiplesampleLinks id={id ?? ""} type={type} relationTable="relatedMultipleSample" />;
      case "relatedBibliography":
        return <BibliographyLinks id={id ?? ""} type={type} />;
      case "relatedAuction":
        return <AuctionLinks id={id ?? ""} type={type} />;
      case "relatedAuthor":
        return <AuthorLinks id={id ?? ""} type={type} />;
      case "relatedExhibition":
        return <ExhibitionLinks id={id ?? ""} type={type} />;
      case "relatedTirage":
        return <TirageLinks id={id ?? ""} type={type} />;
      case "externalLinks":
        return <ExternalLinks id={id ?? ""} type={type} />;
      case "sculptureEditionInExhibitions":
        return (
          <SculptureEditionInExhibitions
            id={id ?? ""}
            type={type}
            exhibition={type === "exhibition" ? (data as Exhibition) : undefined}
            sculptureEdition={
              type === "sculptureEdition" ? (data as SculptureEdition) : undefined
            }
            fields={
              links?.find((link) => link.name === "sculptureEditionInExhibitions")
                ?.fields
            }
          />
        );
      case "sculptureEditionInBibliography":
        return (
          <SculptureEditionInBibliography
            id={id ?? ""}
            type={type}
            bibliography={type === "bibliography" ? (data as Bibliography) : undefined}
            sculptureEdition={
              type === "sculptureEdition" ? (data as SculptureEdition) : undefined
            }
            fields={
              links?.find((link) => link.name === "sculptureEditionInBibliography")
                ?.fields
            }
          />
        );
      case "sculptureEditionInAuction":
        return (
          <SculptureEditionInAuction
            id={id ?? ""}
            type={type}
            auction={type === "auction" ? (data as Auction) : undefined}
            sculptureEdition={
              type === "sculptureEdition" ? (data as SculptureEdition) : undefined
            }
            fields={
              links?.find((link) => link.name === "sculptureEditionInAuction")?.fields
            }
          />
        );
      case "collectorInMultipleprints":
        return (
          <CollectorInMultipleprints
            id={id ?? ""}
            type={type}
            collector={data as Collector}
            fields={
              links?.find((link) => link.name === "collectorInMultipleprints")
                ?.fields
            }
          />
        );
      case "multipleprintInBibliography":
        return (
          <MultipleprintInBibliographyLinks
            id={id ?? ""}
            type={type}
            bibliography={type === "bibliography" ? (data as Bibliography) : undefined}
            multipleprint={type === "multipleprint" ? (data as Multipleprint) : undefined}
            fields={
              links?.find((link) => link.name === "multipleprintInBibliography")
                ?.fields
            }
          />
        );
      case "multipleprintInExhibition":
        return (
          <MultipleprintInExhibtionLinks
            id={id ?? ""}
            type={type}
            exhibition={type === "exhibition" ? (data as Exhibition) : undefined}
            multipleprint={
              type === "multipleprint" ? (data as Multipleprint) : undefined
            }
            fields={
              links?.find((link) => link.name === "multipleprintInExhibition")
                ?.fields
            }
          />
        );
      case "multipleprintInAuction":
        return (
          <MultipleprintInAuctionLinks
            id={id ?? ""}
            type={type}
            auction={type === "auction" ? (data as Auction) : undefined}
            multipleprint={type === "multipleprint" ? (data as Multipleprint) : undefined}
            fields={
              links?.find((link) => link.name === "multipleprintInAuction")
                ?.fields
            }
          />
        );
      case "documentInBibliography":
        return (
          <DocumentInBibliographyLinks
            id={id ?? ""}
            type={type}
            document={data as Document}
            bibliography={data as Bibliography}
            fields={
              links?.find((link) => link.name === "documentInBibliography")
                ?.fields
            }
          />
        );
      case "bibliographyInBibliography":
        return (
          <BibliographyInBibliographyLinks
            id={id ?? ""}
            type={type}
            fields={
              links?.find((link) => link.name === "bibliographyInBibliography")
                ?.fields
            }
          />
        );
      case "photographyInBibliography":
        return (
          <PhotographyInBibliographyLinks
            id={id ?? ""}
            type={type}
            photography={data as Photography}
            bibliography={data as Bibliography}
            fields={
              links?.find((link) => link.name === "photographyInBibliography")
                ?.fields
            }
          />
        );
      case "artworkInExhibitions":
        return (
          <ArtworkInExhibitions
            id={id ?? ""}
            artwork={data as Artwork}
            exhibition={data as Exhibition}
            type={type}
            fields={
              links?.find((link) => link.name === "artworkInExhibitions")
                ?.fields
            }
          />
        );
      case "artworkInBibliography":
        return (
          <ArtworkInBibliographyLinks
            id={id ?? ""}
            artwork={data as Artwork}
            bibliography={data as Bibliography}
            type={type}
            fields={
              links?.find((link) => link.name === "artworkInBibliography")
                ?.fields
            }
          />
        );
      case "artworkInAuctions":
        return (
          <ArtworkInAuctionLinks
            id={id ?? ""}
            type={type}
            auction={data as Auction}
            artwork={data as Artwork}
            fields={
              links?.find((link) => link.name === "artworkInAuctions")?.fields
            }
          />
        );
      case "tirageInExhibition":
        return (
          <TirageInExhibitionLinks
            id={id ?? ""}
            tirage={data as Tirage}
            exhibition={data as Exhibition}
            type={type}
            fields={
              links?.find((link) => link.name === "tirageInExhibition")?.fields
            }
          />
        );
      case "tirageInAuction":
        return (
          <TirageInAuctionLinks
            id={id ?? ""}
            type={type}
            auction={data as Auction}
            tirage={data as Tirage}
            fields={
              links?.find((link) => link.name === "tirageInAuction")?.fields
            }
          />
        );
      case "tirageInBibliography":
        return (
          <TirageInBibliographyLinks
            id={id ?? ""}
            tirage={data as Tirage}
            bibliography={data as Bibliography}
            type={type}
            fields={
              links?.find((link) => link.name === "tirageInBibliography")
                ?.fields
            }
          />
        );
      case "multipleSampleInExhibitions":
        return (
          <MultiplesampleInExhibitionLinks
            id={id ?? ""}
            multiplesample={
              type === "multiplesample" ? (data as Multiplesample) : undefined
            }
            exhibition={
              type === "exhibition" ? (data as Exhibition) : undefined
            }
            type={type}
            fields={
              links?.find((link) => link.name === "multipleSampleInExhibitions")
                ?.fields
            }
            hideExemplaireContextColumns={
              hideExemplaireColumnsInExhibitionLinks === true
            }
          />
        );
      case "multipleSampleInAuction":
        return (
          <MultiplesampleInAuctionLinks
            id={id ?? ""}
            type={type}
            auction={type === "auction" ? (data as Auction) : undefined}
            multiplesample={
              type === "multiplesample" ? (data as Multiplesample) : undefined
            }
            fields={
              links?.find((link) => link.name === "multipleSampleInAuction")
                ?.fields
            }
          />
        );
      case "multipleSampleInBibliography":
        return (
          <MultiplesampleInBibliographyLinks
            id={id ?? ""}
            multiplesample={
              type === "multiplesample" ? (data as Multiplesample) : undefined
            }
            bibliography={
              type === "bibliography" ? (data as Bibliography) : undefined
            }
            type={type}
            fields={
              links?.find(
                (link) => link.name === "multipleSampleInBibliography",
              )?.fields
            }
          />
        );
      default:
        return null;
    }
  }, [
    activeTab,
    data,
    hideExemplaireColumnsInExhibitionLinks,
    id,
    links,
    type,
  ]);

  return (
    <CheckboxGroupMask
      groupName={`linksManagementTableGroup.${type}`}
      label={t("linkedEntries")}
      className={cn("w-full", className)}
    >
      <Tabs
        value={activeTab}
        onValueChange={(val) => {
          setActiveTab(val);
          searchParams.set("linkTab", val);
          setSearchParam(searchParams);
        }}
        className="w-full pr-10"
      >
        <TabsList className="flex-wrap h-auto gap-y-1">
          {links?.map((tab: LinkConfig) => (
            <TabsTrigger
              key={tab.name}
              value={tab.name}
              className="flex items-center gap-2 pr-2"
            >
              {tab.label}
              {!isLoading && (
                <Badge
                  variant={activeTab === tab.name ? "outlinedActive" : "outlined"}
                  className="w-6 h-6"
                >
                  {countRelatedFields?.[tab.name]}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        {links?.map((tab: LinkConfig) => (
          <TabsContent
            key={tab.name}
            value={tab.name}
            className="inline-flex gap-8 w-full data-[state=inactive]:hidden"
          >
            {components}
          </TabsContent>
        ))}
      </Tabs>
    </CheckboxGroupMask>
  );
};

export default LinksManagement;
